import type { GenerateSankeyToolOutput } from '@azymmetric/shared/tools/generate-sankey.tool-config';
import { SankeyModal } from '@components/shared/sankey-modal.component';
import { Sankey } from '@components/shared/sankey.component';
import { normalizeToolOutput } from '@utils/sankey-normalizer.util';
import { calculateSankeySize } from '@utils/sankey.util';
import React, { useMemo } from 'react';

type SankeyArtifactProps = {
  data: GenerateSankeyToolOutput;
  isFullScreenModalOpen: boolean;
  onCloseFullScreenModal: () => void;
};

export const SankeyArtifact: React.FC<SankeyArtifactProps> = (props) => {
  const { data, isFullScreenModalOpen, onCloseFullScreenModal } = props;

  // Normalize the tool output into platform-like SankeyData (ordering, aggregation, scaling)
  // and get chart utilities for formatting and coloring
  const { data: normalizedData, chartUtils } = useMemo(() => {
    if (!data.valid) {
      return {
        data: { nodes: [], links: [] },
        chartUtils: null,
      };
    }
    return normalizeToolOutput(data, {
      colorScheme: 'default',
      scaleMode: 'auto',
      minLinkValue: 3,
    });
  }, [data]);

  // Automatically determine size preset based on data volume
  const sizePreset = useMemo(() => {
    return calculateSankeySize(normalizedData);
  }, [normalizedData]);

  if (!data.valid) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <h3 className="font-semibold text-destructive mb-2">Invalid Sankey Data</h3>
          {data.errors && data.errors.length > 0 && (
            <ul className="space-y-1 text-sm text-destructive">
              {data.errors.map((error, index) => (
                <li key={index}>• {error.message}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 space-y-4">
        {data.errors && data.errors.length > 0 && (
          <div className="rounded-lg border border-amber-500 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-900 mb-1">Warnings:</p>
            <ul className="space-y-1 text-sm text-amber-800">
              {data.errors.map((error, index) => (
                <li key={index}>• {error.message}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="border rounded-lg bg-white">
          <Sankey
            data={normalizedData}
            formatNodeLabel={chartUtils?.formatNodeLabel}
            formatNodeSubtext={chartUtils?.formatNodeSubtext}
            size={sizePreset}
            settings={{
              height: 'auto',
              colorMap: chartUtils?.getColorMap(),
              isAiGenerated: true,
            }}
          />
        </div>
      </div>

      {isFullScreenModalOpen && (
        <SankeyModal
          isOpen={isFullScreenModalOpen}
          onClose={onCloseFullScreenModal}
          title={data.title}
        >
          {({ settings }) => (
            <Sankey
              data={normalizedData}
              formatNodeLabel={chartUtils?.formatNodeLabel}
              formatNodeSubtext={chartUtils?.formatNodeSubtext}
              size={sizePreset}
              settings={{
                colorMap: chartUtils?.getColorMap(),
                isAiGenerated: true,
                ...settings,
              }}
            />
          )}
        </SankeyModal>
      )}
    </>
  );
};
